import {ActionList, ActionMenu, Button, IconButton, Truncate} from '@primer/react'
import {useMediaQuery} from '@github-ui/use-media-query'
import {ChecklistIcon, InfinityIcon, TriangleDownIcon} from '@primer/octicons-react'
import {clsx} from 'clsx'

import type {McpPermissionMode} from '../../types/api-task'
import styles from './McpPermissionModeSelector.module.css'

const MCP_PERMISSION_MODES: McpPermissionMode[] = ['default', 'allow_all']

const MCP_PERMISSION_MODE_INFO: Record<
  McpPermissionMode,
  {icon: typeof ChecklistIcon; label: string; description: string}
> = {
  default: {
    icon: ChecklistIcon,
    label: 'Interactive',
    description: 'Ask for permission',
  },
  allow_all: {
    icon: InfinityIcon,
    label: 'Autopilot',
    description: 'Run all tools without asking',
  },
}

export function McpPermissionModeSelector({
  selectedMode,
  onSelectMode,
  forceIconOnly = false,
  hideLeadingVisual = false,
  triggerClassName,
}: {
  selectedMode: McpPermissionMode
  onSelectMode: (mode: McpPermissionMode) => void
  forceIconOnly?: boolean
  hideLeadingVisual?: boolean
  triggerClassName?: string
}) {
  const selectedInfo = MCP_PERMISSION_MODE_INFO[selectedMode]
  // Collapse with other toolbar selectors so controls stay aligned.
  const isNarrow = useMediaQuery('(max-width: calc(80rem - 0.02px))')
  const showIconOnly = forceIconOnly || isNarrow
  const buttonStyle = hideLeadingVisual ? {border: 'none'} : undefined

  return (
    <ActionMenu>
      <ActionMenu.Anchor>
        {showIconOnly ? (
          <IconButton
            type="button"
            icon={selectedInfo.icon}
            aria-label={`Select MCP permission mode, current: ${selectedInfo.label}`}
            variant="invisible"
            size="medium"
            className={styles.selectorButton}
            style={buttonStyle}
          />
        ) : (
          <Button
            type="button"
            leadingVisual={hideLeadingVisual ? undefined : selectedInfo.icon}
            trailingAction={TriangleDownIcon}
            aria-label={`Select MCP permission mode, current: ${selectedInfo.label}`}
            variant="invisible"
            size="medium"
            className={clsx(styles.selectorButton, triggerClassName)}
            style={buttonStyle}
          >
            <Truncate title={selectedInfo.label} maxWidth="100%">
              {selectedInfo.label}
            </Truncate>
          </Button>
        )}
      </ActionMenu.Anchor>
      <ActionMenu.Overlay width="medium">
        <ActionList>
          <ActionList.Group selectionVariant="single">
            <ActionList.GroupHeading>Mode</ActionList.GroupHeading>
            {MCP_PERMISSION_MODES.map(mode => {
              const info = MCP_PERMISSION_MODE_INFO[mode]
              return (
                <ActionList.Item key={mode} selected={mode === selectedMode} onSelect={() => onSelectMode(mode)}>
                  <ActionList.LeadingVisual>
                    <info.icon />
                  </ActionList.LeadingVisual>
                  {info.label}
                  <ActionList.Description variant="block">{info.description}</ActionList.Description>
                </ActionList.Item>
              )
            })}
          </ActionList.Group>
        </ActionList>
      </ActionMenu.Overlay>
    </ActionMenu>
  )
}
